import React, { useState, useEffect } from "react";
import { getResearchAdmin, createResearch, updateResearch, deleteResearch } from "../../../APIClient/apiClient";
import Toast from "../../../component/Toast";
import "../../../style/admin/AnnouncementManager.css";

const ResearchManager = () => {
  const [researches, setResearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [activeTab, setActiveTab] = useState("en");
  const [formData, setFormData] = useState({
    en: { title: "", abstract: "", contributors: "", start_date_text: "", end_date_text: "", funding: "", notes: "" },
    vi: { title: "", abstract: "", contributors: "", start_date_text: "", end_date_text: "", funding: "", notes: "" },
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchResearches();
  }, []);

  const fetchResearches = async () => {
    setLoading(true);
    try {
      const data = await getResearchAdmin();
      setResearches(data);
    } catch (err) {
      setToast({ message: "Failed to load research projects", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (language, field, value) => {
    setFormData({
      ...formData,
      [language]: {
        ...formData[language],
        [field]: value,
      },
    });
  };

  const handleMetaChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        translations: {
          en: formData.en,
          vi: formData.vi,
        },
        sort_order: formData.sort_order,
        is_active: formData.is_active,
      };

      if (editingId) {
        await updateResearch(editingId, payload);
      } else {
        await createResearch(payload);
      }
      fetchResearches();
      resetForm();
      setToast({ message: "Research project saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save research project", type: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this research project?")) {
      try {
        await deleteResearch(id);
        fetchResearches();
        setToast({ message: "Research project deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete research project", type: "error" });
      }
    }
  };

  const handleEdit = (research) => {
    setFormData({
      en: {
        title: research.translations.en?.title || "",
        abstract: research.translations.en?.abstract || "",
        contributors: research.translations.en?.contributors || "",
        start_date_text: research.translations.en?.start_date_text || "",
        end_date_text: research.translations.en?.end_date_text || "",
        funding: research.translations.en?.funding || "",
        notes: research.translations.en?.notes || "",
      },
      vi: {
        title: research.translations.vi?.title || "",
        abstract: research.translations.vi?.abstract || "",
        contributors: research.translations.vi?.contributors || "",
        start_date_text: research.translations.vi?.start_date_text || "",
        end_date_text: research.translations.vi?.end_date_text || "",
        funding: research.translations.vi?.funding || "",
        notes: research.translations.vi?.notes || "",
      },
      sort_order: research.sort_order,
      is_active: research.is_active,
    });
    setEditingId(research.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      en: { title: "", abstract: "", contributors: "", start_date_text: "", end_date_text: "", funding: "", notes: "" },
      vi: { title: "", abstract: "", contributors: "", start_date_text: "", end_date_text: "", funding: "", notes: "" },
      sort_order: 0,
      is_active: true,
    });
    setEditingId(null);
    setShowForm(false);
    setActiveTab("en");
  };

  if (loading) return <div className="admin-manager-loading">Loading research projects...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="admin-manager-header">
        <button className="admin-manager-btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? "Cancel" : "Add New Research Project"}
        </button>
      </div>

      {showForm && (
        <div className="admin-manager-form">
          <h3>{editingId ? "Edit Research Project" : "Create New Research Project"}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-tabs">
              <button type="button" className={activeTab === "en" ? "active" : ""} onClick={() => setActiveTab("en")}>
                English
              </button>
              <button type="button" className={activeTab === "vi" ? "active" : ""} onClick={() => setActiveTab("vi")}>
                Vietnamese
              </button>
            </div>

            {activeTab === "en" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Title (EN) *</label>
                  <input
                    type="text"
                    value={formData.en.title}
                    onChange={(e) => handleInputChange("en", "title", e.target.value)}
                    placeholder="English title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Abstract (EN)</label>
                  <textarea
                    value={formData.en.abstract}
                    onChange={(e) => handleInputChange("en", "abstract", e.target.value)}
                    placeholder="English abstract"
                    rows="5"
                  />
                </div>
                <div className="form-group">
                  <label>Contributors (EN)</label>
                  <input
                    type="text"
                    value={formData.en.contributors}
                    onChange={(e) => handleInputChange("en", "contributors", e.target.value)}
                    placeholder="Contributor names"
                  />
                </div>
                <div className="form-group">
                  <label>Start Date Text (EN)</label>
                  <input
                    type="text"
                    value={formData.en.start_date_text}
                    onChange={(e) => handleInputChange("en", "start_date_text", e.target.value)}
                    placeholder="e.g., Jan 01, 2024"
                  />
                </div>
                <div className="form-group">
                  <label>End Date Text (EN)</label>
                  <input
                    type="text"
                    value={formData.en.end_date_text}
                    onChange={(e) => handleInputChange("en", "end_date_text", e.target.value)}
                    placeholder="e.g., Dec 31, 2026 or Ongoing"
                  />
                </div>
                <div className="form-group">
                  <label>Funding (EN)</label>
                  <input
                    type="text"
                    value={formData.en.funding}
                    onChange={(e) => handleInputChange("en", "funding", e.target.value)}
                    placeholder="Funding source"
                  />
                </div>
                <div className="form-group">
                  <label>Notes (EN)</label>
                  <textarea
                    value={formData.en.notes}
                    onChange={(e) => handleInputChange("en", "notes", e.target.value)}
                    placeholder="English notes"
                  />
                </div>
              </div>
            )}

            {activeTab === "vi" && (
              <div className="form-section">
                <div className="form-group">
                  <label>Title (VI) *</label>
                  <input
                    type="text"
                    value={formData.vi.title}
                    onChange={(e) => handleInputChange("vi", "title", e.target.value)}
                    placeholder="Vietnamese title"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Abstract (VI)</label>
                  <textarea
                    value={formData.vi.abstract}
                    onChange={(e) => handleInputChange("vi", "abstract", e.target.value)}
                    placeholder="Vietnamese abstract"
                    rows="5"
                  />
                </div>
                <div className="form-group">
                  <label>Contributors (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.contributors}
                    onChange={(e) => handleInputChange("vi", "contributors", e.target.value)}
                    placeholder="Tên người đóng góp"
                  />
                </div>
                <div className="form-group">
                  <label>Start Date Text (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.start_date_text}
                    onChange={(e) => handleInputChange("vi", "start_date_text", e.target.value)}
                    placeholder="e.g., 01/01/2024"
                  />
                </div>
                <div className="form-group">
                  <label>End Date Text (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.end_date_text}
                    onChange={(e) => handleInputChange("vi", "end_date_text", e.target.value)}
                    placeholder="e.g., 31/12/2026 hoặc Đang thực hiện"
                  />
                </div>
                <div className="form-group">
                  <label>Funding (VI)</label>
                  <input
                    type="text"
                    value={formData.vi.funding}
                    onChange={(e) => handleInputChange("vi", "funding", e.target.value)}
                    placeholder="Nguồn tài trợ"
                  />
                </div>
                <div className="form-group">
                  <label>Notes (VI)</label>
                  <textarea
                    value={formData.vi.notes}
                    onChange={(e) => handleInputChange("vi", "notes", e.target.value)}
                    placeholder="Vietnamese notes"
                  />
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Sort Order</label>
                <input type="number" value={formData.sort_order} onChange={(e) => handleMetaChange("sort_order", parseInt(e.target.value))} />
              </div>
              <div className="form-group">
                <label>Active</label>
                <input type="checkbox" checked={formData.is_active} onChange={(e) => handleMetaChange("is_active", e.target.checked)} />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? "Update" : "Create"} Research Project
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-manager-table">
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>Title (EN)</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Funding</th>
              <th>Active</th>
              <th>Sort Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {researches.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                  No research projects found
                </td>
              </tr>
            ) : (
              researches.map((research, index) => (
                <tr key={research.id}>
                  <td>{index + 1}</td>
                  <td>{research.translations.en?.title || "N/A"}</td>
                  <td>{research.translations.en?.start_date_text || "N/A"}</td>
                  <td>{research.translations.en?.end_date_text || "N/A"}</td>
                  <td>{research.translations.en?.funding || "N/A"}</td>
                  <td>{research.is_active ? "Yes" : "No"}</td>
                  <td>{research.sort_order}</td>
                  <td className="action-buttons">
                    <button className="btn-edit" onClick={() => handleEdit(research)}>
                      Edit
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(research.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResearchManager;
